import { useEffect, useState } from "react";
import { Save, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { invalidateSettings } from "../../hooks/useSettings";
import { ImagePicker } from "./PostsPanel";

const SEO_PAGES = [
    { key: "home", label: "الصفحة الرئيسية", testid: "kw-page-home" },
    { key: "blog", label: "صفحة المدونة", testid: "kw-page-blog" },
];

function KeywordEditor({ label, keywords, onChange, testid }) {
    const [draft, setDraft] = useState("");

    const add = () => {
        const v = draft.trim();
        if (!v) return;
        if (!keywords.includes(v)) onChange([...keywords, v]);
        setDraft("");
    };

    return (
        <div className="border border-white/10 rounded-2xl p-4 space-y-3" data-testid={testid}>
            <p className="text-xs font-bold text-slate-300">{label}</p>
            <div className="flex flex-wrap gap-2">
                {keywords.map((kw) => (
                    <span
                        key={kw}
                        className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/25 text-amber-300 text-xs font-bold rounded-full px-3 py-1.5"
                        data-testid={`${testid}-chip`}
                    >
                        {kw}
                        <button
                            type="button"
                            onClick={() => onChange(keywords.filter((k) => k !== kw))}
                            data-testid={`${testid}-remove`}
                            className="text-amber-400/60 hover:text-red-400 transition-colors duration-200"
                            aria-label={`حذف ${kw}`}
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                {keywords.length === 0 && (
                    <span className="text-[11px] text-slate-600">لا توجد كلمات — أضف أول كلمة</span>
                )}
            </div>
            <div className="flex gap-2">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder="اكتب كلمة مفتاحية ثم اضغط إضافة"
                    data-testid={`${testid}-input`}
                    className="flex-1 bg-[#0A1228]/70 border border-white/10 rounded-xl py-2 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300"
                />
                <button
                    type="button"
                    onClick={add}
                    data-testid={`${testid}-add`}
                    className="flex items-center gap-1 bg-amber-400/15 border border-amber-400/40 text-amber-300 text-xs font-extrabold rounded-xl px-4 hover:bg-amber-400 hover:text-[#0A1228] transition-colors duration-300"
                >
                    <Plus className="w-3.5 h-3.5" /> إضافة
                </button>
            </div>
        </div>
    );
}

const SEO_FIELDS = [
    { key: "ga_id", label: "Google Analytics — معرف القياس (G-XXXXXXX)", testid: "settings-ga" },
    { key: "gsc_verification", label: "Google Search Console — كود التحقق", testid: "settings-gsc" },
];

const CONTACT_FIELDS = [
    { key: "contact_mobile", label: "رقم الجوال / واتساب", testid: "settings-mobile" },
    { key: "contact_landline", label: "الهاتف الثابت", testid: "settings-landline" },
    { key: "contact_email", label: "البريد الإلكتروني", testid: "settings-email" },
];

const MAP_FIELDS = [
    { key: "maps_link", label: "رابط خرائط جوجل (المشاركة)", testid: "settings-maps-link" },
    { key: "maps_query", label: "نص البحث في الخريطة المدمجة", testid: "settings-maps-query" },
];

export const ContentPanel = () => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
    }, []);

    if (!form) return <p className="text-slate-500 text-sm">جاري التحميل...</p>;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/settings", form);
            invalidateSettings();
            toast.success("تم حفظ الإعدادات — تظهر التغييرات مباشرة في الموقع");
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "w-full bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

    return (
        <div data-testid="admin-content-panel" className="max-w-2xl">
            <h2 className="font-display font-bold text-xl text-white mb-2">محتوى الموقع والإعدادات</h2>
            <p className="text-sm text-slate-500 mb-6">تحكم كامل في الواجهة والنصوص والتواصل والخريطة وأكواد جوجل — بدون مبرمج</p>

            <form onSubmit={save} className="space-y-5">
                <div className="spotlight-card border border-white/10 rounded-3xl p-6 space-y-5">
                    <p className="font-display font-bold text-amber-400 text-sm">الواجهة والمظهر</p>
                    <div>
                        <p className="text-xs font-bold text-slate-400 mb-2">صورة الواجهة الرئيسية (Hero)</p>
                        <ImagePicker value={form.hero_image || ""} onChange={(v) => set("hero_image", v)} testid="settings-hero-image" />
                        <p className="text-[11px] text-slate-600 mt-1">اتركه فارغاً لاستخدام الصورة الافتراضية (واجهة المحل ليلاً)</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <label className="flex items-center gap-3 text-xs text-slate-300">
                            <span className="flex-1">لون الخلفية الرئيسي</span>
                            <input type="color" value={form.bg_color || "#0A1228"} onChange={(e) => set("bg_color", e.target.value)} data-testid="settings-bg-color" className="w-12 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                        </label>
                        <label className="flex items-center gap-3 text-xs text-slate-300">
                            <span className="flex-1">اللون المميز (الذهبي)</span>
                            <input type="color" value={form.accent_color || "#FBBF24"} onChange={(e) => set("accent_color", e.target.value)} data-testid="settings-accent-color" className="w-12 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                        </label>
                    </div>
                    <p className="text-[11px] text-slate-600">لإعادة اللون الافتراضي: امسح الحقل واحفظ</p>
                    <input value={form.slogan_ar || ""} onChange={(e) => set("slogan_ar", e.target.value)} placeholder="الشعار التسويقي (عربي) — الافتراضي: مع ميرو… إطارك أمانك" data-testid="settings-slogan-ar" className={inputCls} />
                    <input value={form.slogan_en || ""} onChange={(e) => set("slogan_en", e.target.value)} placeholder="Slogan (English)" data-testid="settings-slogan-en" className={inputCls} dir="ltr" />
                </div>

                <div className="spotlight-card border border-white/10 rounded-3xl p-6 space-y-5">
                    <p className="font-display font-bold text-amber-400 text-sm">معلومات التواصل والعنوان</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {CONTACT_FIELDS.map((f) => (
                            <div key={f.key} className={f.key === "contact_email" ? "sm:col-span-2" : ""}>
                                <p className="text-xs font-bold text-slate-400 mb-2">{f.label}</p>
                                <input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={f.testid} className={inputCls} dir="ltr" />
                            </div>
                        ))}
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 mb-2">العنوان (عربي)</p>
                        <input value={form.address_ar || ""} onChange={(e) => set("address_ar", e.target.value)} data-testid="settings-address-ar" className={inputCls} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 mb-2">العنوان (إنجليزي)</p>
                        <input value={form.address_en || ""} onChange={(e) => set("address_en", e.target.value)} data-testid="settings-address-en" className={inputCls} dir="ltr" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 mb-2">ساعات العمل (عربي)</p>
                        <input value={form.hours_ar || ""} onChange={(e) => set("hours_ar", e.target.value)} data-testid="settings-hours-ar" className={inputCls} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 mb-2">ساعات العمل (إنجليزي)</p>
                        <input value={form.hours_en || ""} onChange={(e) => set("hours_en", e.target.value)} data-testid="settings-hours-en" className={inputCls} dir="ltr" />
                    </div>
                    <div className="grid gap-4">
                        {MAP_FIELDS.map((f) => (
                            <div key={f.key}>
                                <p className="text-xs font-bold text-slate-400 mb-2">{f.label}</p>
                                <input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={f.testid} className={inputCls} dir="ltr" />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="spotlight-card border border-white/10 rounded-3xl p-6 space-y-5">
                    <p className="font-display font-bold text-amber-400 text-sm">إعدادات SEO وجوجل</p>
                    <div className="space-y-4">
                        <div>
                            <p className="text-xs font-bold text-slate-400 mb-1">الكلمات المفتاحية لكل صفحة</p>
                            <p className="text-[11px] text-slate-600">تظهر في meta keywords وتحسّن ظهور الموقع في جوجل — كلمات كل مقال على حدة تُدار من تبويب المقالات</p>
                        </div>
                        {SEO_PAGES.map((p) => (
                            <KeywordEditor
                                key={p.key}
                                label={p.label}
                                keywords={(form.page_keywords || {})[p.key] || []}
                                onChange={(list) => set("page_keywords", { ...(form.page_keywords || {}), [p.key]: list })}
                                testid={p.testid}
                            />
                        ))}
                    </div>
                    {SEO_FIELDS.map((f) => (
                        <div key={f.key}>
                            <p className="text-xs font-bold text-slate-400 mb-2">{f.label}</p>
                            <input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={f.testid} className={inputCls} dir="ltr" />
                        </div>
                    ))}
                </div>

                <button
                    type="submit"
                    disabled={saving}
                    data-testid="settings-save-button"
                    className="w-full flex items-center justify-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl py-3.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "جاري الحفظ..." : "حفظ كل الإعدادات"}
                </button>
            </form>
        </div>
    );
};
